/**
 * Рассылки по отдельному каналу — Telegram / Push / MAX (сверх legacy) «по аналогии с Email».
 *
 * Отдельная страница на канал: свой список получателей + сообщение + отправка. Роуты в
 * App.tsx (telegram_msgs / push_msgs / max_msgs), пункты меню добавлены в новой админке
 * (backend Modules::adminMenu, без правки таблицы modules — её читает и старый сайт).
 *
 * Получатель здесь — УЧАСТОК, а не абонент с адресом: подписка на эти каналы это
 * устройство (rw_push_tokens) либо привязанный чат (rw_telegram_links / rw_max_links).
 * Поэтому в списке — участки, а на сервер уходят их id (area_fk).
 *
 * Отличие Push: у него есть отдельный ЗАГОЛОВОК (как на экране уведомления в приложении),
 * поэтому у push-страницы два поля — заголовок и текст; у Telegram/MAX только текст.
 *
 * Разметка и классы — те же, что у страницы Email (admin_email_info.tpl): add-form,
 * кнопка «Список рассылки» (.bt.orange), окно со списком (<Modal>), счётчик .margin_null.
 */

import { useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { Modal } from '@/components/Modal'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

/** Участок-получатель канала (GET /api/admin/mailing/{channel}). */
interface AreaRecipient {
  area_fk: number
  name: string
  /** ФИО собственника — как в списке Email-рассылки. */
  fio: string
}

interface RecipientsResponse {
  areas: AreaRecipient[]
  /** Только для push: сколько всего устройств зарегистрировано. */
  devices?: number
  blocker: string
}

interface SendResponse {
  sent?: number[]
  errors?: string[]
  blocker: string
  message?: string
}

/** Подтверждение перед реальной отправкой (сообщения уходят жителям). */
function confirmSend(what: string): boolean {
  return window.confirm(
    `ВНИМАНИЕ! Сообщение будет ОТПРАВЛЕНО РЕАЛЬНЫМ ЖИТЕЛЯМ (${what}).\n` +
      'Отменить отправку после подтверждения нельзя. Продолжить?',
  )
}

/** Настройки конкретного канала: адрес API, название, нужен ли заголовок (как у push). */
interface ChannelConfig {
  path: string // '/api/admin/mailing/telegram' и т.п.
  label: string // «Telegram» — для подтверждения и заголовка
  withTitle: boolean // push: заголовок + текст; telegram/max: только текст
}

const TELEGRAM: ChannelConfig = { path: '/api/admin/mailing/telegram', label: 'Telegram', withTitle: false }
const MAX: ChannelConfig = { path: '/api/admin/mailing/max', label: 'Max', withTitle: false }
const PUSH: ChannelConfig = { path: '/api/admin/mailing/push', label: 'Push', withTitle: true }

export function TelegramMailingPage() {
  return <ChannelMailing config={TELEGRAM} />
}

export function MaxMailingPage() {
  return <ChannelMailing config={MAX} />
}

export function PushMailingPage() {
  return <ChannelMailing config={PUSH} />
}

function ChannelMailing({ config }: { config: ChannelConfig }) {
  useLegacyCss('admin', 'newapproach')

  const { data, reload } = useApi<RecipientsResponse>(config.path)

  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState<Record<number, boolean>>({})
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [blocker, setBlocker] = useState('')
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const recipients = data?.areas ?? []

  useEffect(() => {
    if (data !== null) setBlocker(data.blocker)
  }, [data])

  const areasKey = (data?.areas ?? []).map((item) => item.area_fk).join(',')

  /**
   * Отметки «выбран» ставим ЗАНОВО, только когда сменился САМ СПИСОК получателей —
   * а не на каждое обновление данных (SWR перезапрашивает при возврате на вкладку, и
   * выбор, расставленный руками, иначе затирался бы «отмечены все»).
   */
  useEffect(() => {
    if (data === null) return
    const state: Record<number, boolean> = {}
    for (const item of data.areas) state[item.area_fk] = true // по умолчанию все отмечены
    setSelected(state)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно только по составу списка
  }, [areasKey])

  const count = Object.values(selected).filter(Boolean).length

  const selectAll = (value: boolean): void => {
    const state: Record<number, boolean> = {}
    for (const item of recipients) state[item.area_fk] = value
    setSelected(state)
  }

  const send = async (): Promise<void> => {
    if (!confirmSend(`${count} ${config.label}`)) return

    const areas = recipients.filter((item) => selected[item.area_fk] === true).map((item) => item.area_fk)

    try {
      const body = config.withTitle
        ? { blocker, areas, title, body: message }
        : { blocker, areas, message }
      const result = await api.post<SendResponse>(config.path, body)
      setOk(result.message !== undefined ? [result.message] : [])
      setError(result.errors ?? [])
      setBlocker(result.blocker) // токен одноразовый: получаем новый
      if ((result.errors ?? []).length === 0) {
        setTitle('')
        setMessage('')
      }
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при отправке'])
      setOk([])
      reload() // токен мог быть погашен — забираем свежий
    }
  }

  return (
    <form
      className="add-form"
      method="POST"
      id="form_id"
      onSubmit={(e) => {
        e.preventDefault()
        void send()
      }}
    >
      <button type="button" className="bt orange" onClick={() => setModalOpen(true)}>
        Список рассылки
      </button>
      <p className="margin_null">
        Выбрано: <span id="abon">{count}</span> уч.
        {config.label === 'Push' && data?.devices !== undefined && ` · устройств всего: ${data.devices}`}
      </p>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Список рассылки">
        <a
          style={{ transform: 'scale(0.9)', cursor: 'pointer' }}
          className="bt orange"
          onClick={() => selectAll(true)}
        >
          Выбрать все
        </a>
        <a
          style={{ transform: 'scale(0.9)', cursor: 'pointer' }}
          className="bt red"
          onClick={() => selectAll(false)}
        >
          Убрать все
        </a>
        <button
          style={{ transform: 'scale(0.9)' }}
          type="button"
          className="bt green"
          onClick={() => setModalOpen(false)}
        >
          Сохранить
        </button>

        <table className="table table-hover" style={{ marginTop: '15px' }}>
          <thead>
            <tr>
              <th />
              <th className="mob_td_areaname">Участок</th>
              <th className="mob_td_fio">ФИО</th>
            </tr>
          </thead>
          <tbody>
            {recipients.length === 0 && (
              <tr>
                <td colSpan={3} style={{ textAlign: 'center' }}>
                  Нет участков, подключивших {config.label}.
                </td>
              </tr>
            )}
            {recipients.map((item) => (
              <tr
                className="RowClass"
                key={item.area_fk}
                // legacy: клик по строке переключает чекбокс
                onClick={(e) => {
                  if ((e.target as HTMLElement).tagName === 'INPUT') return
                  setSelected({ ...selected, [item.area_fk]: selected[item.area_fk] !== true })
                }}
              >
                <td className="checkbox_modal">
                  <input
                    type="checkbox"
                    name="areas[]"
                    value={item.area_fk}
                    checked={selected[item.area_fk] === true}
                    onChange={(e) => setSelected({ ...selected, [item.area_fk]: e.target.checked })}
                  />
                </td>
                <td className="mob_td_areaname">{item.name}</td>
                <td className="mob_td_fio">{item.fio || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>

      <div className="row" style={{ margin: '0px' }}>
        <div className="col-md-6">
          {ok.map((text, index) => (
            <p key={index} style={{ float: 'left', color: 'green' }}>
              {text}
            </p>
          ))}
          {error.map((text, index) => (
            <span key={index} style={{ color: '#ce4844', float: 'left' }}>
              {text}
            </span>
          ))}
        </div>
      </div>

      {/* Push: отдельный заголовок (как на экране уведомления). Telegram/MAX — только текст. */}
      {config.withTitle && (
        <div
          className="form-control news-input"
          style={{ paddingBottom: '6px', display: 'table', marginBottom: '10px' }}
        >
          <input
            type="text"
            name="title"
            style={{ display: 'table-cell', border: 0, outline: 0, width: '100%' }}
            value={title}
            placeholder="Заголовок уведомления"
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
      )}

      {/* Только текст: Telegram/MAX не понимают разметку, поэтому обычная textarea, а не WYSIWYG. */}
      <textarea
        id="textarea"
        name="message"
        style={{ height: '250px', width: '100%' }}
        value={message}
        placeholder={config.withTitle ? 'Текст уведомления' : 'Текст сообщения'}
        onChange={(e) => setMessage(e.target.value)}
      />

      <div style={{ textAlign: 'center' }}>
        <input type="hidden" name="blocker" value={blocker} />
        <input type="submit" name="send" className="bt" value="Отправить" />
      </div>
    </form>
  )
}
